import { DocumentReference, Timestamp } from 'firebase/firestore';
import { AssetType, EventLogActionType, EventLogSource, JobStatus, VisitType, WorkListType, EnginnerCategory, ContractLineStatus } from './enums';

export interface Template {
  name: string;
}

export interface TemplateOutlet {
  outletRef: DocumentReference;
  outlet: Record<string, any>;
}

export interface Mail {
  to: string[];
  cc: string[];
  message: MailMessage;
}

export interface FailedEmail {
  id: string;
  to: string[];
  cc: string[];
  subject: string;
  error: string;
  response: string;
  resentAt: Date | null;
  startTime: Date | null;
  attachments: MailAttachment[];
}

export interface MailMessage {
  subject: string;
  text: string;
  html?: string;
  attachments: MailAttachment[];
}

export interface MailAttachment {
  filename: string;
  content: string;
  contentType?: string;
  encoding?: string;
}

export interface SampleSet {
  name: string;
  status: string;
}

export interface Sample {
  outletRef: DocumentReference;
  outletId: string | null,
  siteId: string | null,
  buildingId: string | null,
  type: string;
  status: string;     // SampleStatus enum
  taken: Timestamp;
  takenBy: string;
  takenById: string | null,
  updatedById: string | null,
  sent: Timestamp;
  temperature: string;
  hotPipeworkTemperature?: string;   // TemporaryTest only
  coldPipeworkTemperature?: string;  // TemporaryTest only
  failsafePassed?: boolean;          // TemporaryTest only
  outletAdjusted?: boolean;          // TemporaryTest only
  flush: string;
  timerMinutes: number;
  barcodes: Record<string, string>;
  outlet: Record<string, any>;
}

export interface Option {
  value: string;
  viewValue: string;
}

export interface ListOption {
  value: string;
  viewValue: string;
  selected: boolean;
}

export interface VisitTypeListOption {
  value: VisitType;
  viewValue: string;
  selected: boolean;
}

export interface WorkListSet {
  name: string;
  status: string;
  assetType: AssetType;
}

export interface WorkListTask {
  outletRef: DocumentReference;
  outletId: string | null,
  siteId: string | null,
  buildingId: string | null,
  type: string;
  status: string;     // SampleStatus enum
  taken: Timestamp;
  takenBy: string;
  takenById: string | null,
  updatedById: string | null,
  sent: Timestamp;
  outlet: Record<string, any>;
  hotFeedTemperature?: number;
  coldFeedTemperature?: number;
  outputTemperature?: number;
  preWorksPhoto?: string;
  unableToAdjustTemperature?: boolean;
  strainersCleanedAndDescaled?: boolean;
  internalMechanismRemovedAndCleaned?: boolean;
  tmtTmvReturnedToService?: boolean;
  workAreaLeftCleanAndTidy?: boolean;
  anyLeaksOnPipeworkOrValves?: boolean;
  failSafeCheckPassed?: boolean;
  isolationPresent?: boolean;
  showerheadCleaned?: boolean;
  postWorksPhoto?: string;
  serviceNotes?: string;
  notesOrNonCompliance?: string;
  startFlushingTime?: string;
  endFlushingTime?: string;
  flushTime?: number;
}

export interface EventDetails {
  id?: string,
  jobId: string,
  jobType: WorkListType,
  resourceId: string,
  title: string,
  start: Date,
  end: Date,
  icon: string,
  description: string, 
  status: JobStatus;
  editable: boolean,
  resourceEditable: boolean,
  backgroundColor?: string,
  borderColor?: string,
  editedByUser?: boolean,
  estateWhat3wordsLocation?: string,
  addressPostCode?: string,
  bestParkingWhat3wordsLocation?: string,
  clientRelationsResponsibility?: string,
  siteId: string,
  cancelNotes?: string,
  isOvernight: boolean,
  isInstallIncluded: boolean,
  isSiteSurveyIncluded: boolean,
  ruleId: string,
  salePrice: number | null,
  totalExpenses: number | null,
  equipmentAndMaterialsNeeded: string | null,
  equipmentAndMaterialsUsed: boolean,
  equipmentAndMaterialsUsedDesc: string | null,
  orderNumber: string | null,
  buildingId: string | null,
  kpi: number | null,
  samplingFlexibility: string | null,
  reportingMethod?: string | null,
  reportingMethodOther?: string | null,
  jobComments?: string | null,
  actualCompleteKpi: number | null,
  kpiMissed: boolean,
  kpiMissedReason: string | null,
  kpiIssueReason?: string | null,
  clientRelationsPersonNotified?: string | null
}

export interface EventSummary {
  title: string;
  description: string;
  resourceId: string;
  siteId?: string;
  start: Date;
  end: Date;
  milesTo: number | null;
  drivingTimeTo: number | null;
  priceTo: number | null;
  milesBack: number | null;
  drivingTimeBack: number | null;
  priceBack: number | null;
  addressFrom?: string;
  addressBack?: string;
  profitabilityCosts?: number;
  workItemsProfitability?: string;
  engineerJobCosts: number;
  isOvernight: boolean;
  status: JobStatus;
  salePrice: number | null;
  totalExpenses: number | null;
  inProgressDate?: Date | null;
  completedDate?: Date | null;
  kpi?: number | null;
  actualCompleteKpi?: number | null;
  kpiMissedReason: string | null;
}

export interface EngineersEventsSummary {
  name: string;
  drivingCosts: number;
  profitabilityCosts: number;
  engineersJobCosts: number;
  totalExpensesCosts: number;
  profit: number;
  profitMargin: number;
  events: EventSummary[];
}

export interface WorkDoneList {
  name: string;
  value: number;
  type: string;
  count: number;
}

export interface PriceList {
  pricePerMile: number;
  priceInitialPerCopper: number;
  priceInitialPerSilver: number;
  priceInitialPerLegionella: number;
  priceInitialPerTVC: number;
  priceInitialPerPseudomonas: number;
  priceInitialPerOtherSample: number;
  priceLabPerCopper: number;
  priceLabPerSilver: number;
  priceLabPerLegionella: number;
  priceLabPerTVC: number;
  priceLabPerPseudomonas: number;
  priceLabPerOtherSample: number;
  pricePerCopper: number;
  pricePerSilver: number;
  pricePerLegionella: number;
  pricePerTVC: number;
  pricePerPseudomonas: number;
  pricePerOtherSample: number;
  pricePerFailsafe: number;
  pricePerMaintenance: number;
  pricePerShowerClean: number;
  pricePerTemperature: number;
  pricePerFlushing: number;
  pricePerSmallTankClean: number;
  pricePerMediumTankClean: number;
  pricePerLargeTankClean: number;
  pricePerExtraLargeTankClean: number;
  pricePerTankInspection: number;
  pricePerCalorifierClean: number;
  pricePerCalorifierInspection: number;
  pricePerBlowdown: number;
  pricePerDisinfection: number;
  pricePerKb1: number;
  pricePerKb2: number;
  pricePerKb3: number;
  costPerHourEngCategoryA: number;
  costPerHourEngCategoryB: number;
  costPerHourEngCategoryC: number;
  costOfOvernight: number;
  pricePerCopperPodRevenue: number;
  pricePerSilverPodRevenue: number;
  pricePerCopperPodCost: number;
  pricePerSilverPodCost: number;
  pricePerCopperPodProfit: number;
  pricePerSilverPodProfit: number;
  pricePerSystemCommission: number;
  pricePerSystemInstall: number;
  pricePerSiteSurvey: number;
}

export interface EventLog {
  id: string,
  eventLogSource: EventLogSource,
  eventLogActionType: EventLogActionType,
  eventTime: Date | null,
  userId: string,
  userEmail: string,
  modifiedItemId: string,
  siteId: string,
  siteName?: string,
  value: any
}

export interface ContractLine {
  id?: string;
  eventReference: string;
  type: string | null;
  flexibility: string | null;
  reportingMethod?: string | null;
  reportingMethodOther?: string | null;
  jobComments?: string | null;
  taskDescription: string;
  salePrice: number | null;
  equipmentNeeded: string;
  poNumber: string;
  engineerBand: EnginnerCategory | null;
  frequencyOfVisit: string | null;
  startDate: Date | null;
  eventsPerFrequency: number;
  rules: string;
  rulesDescription: string;
  engineerKpiPerEvent: number | null;
  duration: string | null;
  status: ContractLineStatus;
  tasksRequired: number | null;
  tasksGenerated: number | null;
}

export interface AttachmentResult {
  attachments: MailAttachment[];
  generatedInBatches: boolean;
}

export interface TmvRules {
  openRules?: {
    issueReasons?: string[];
    issueReasonsSeverity?: Record<string, string>; // Map issue reason to severity (Low, Medium, High)
  };
  closeRules?: {
    isAutomatic?: boolean;
    successfulChecksRequired?: number;
  };
}

// Chlorine Dioxide bands: Red (=0), Amber (0 - greenMin), Green (greenMin - greenMax), Amber (greenMax - redMin), Red (>=redMin)
export interface TemperatureThresholds {
  chlorineDioxideGreenMin?: number;   // 0.1
  chlorineDioxideGreenMax?: number;   // 0.5
  chlorineDioxideRedMin?: number;     // 1.0
}

export const DEFAULT_TEMPERATURE_THRESHOLDS: TemperatureThresholds = {
  chlorineDioxideGreenMin:  0.1,
  chlorineDioxideGreenMax:  0.5,
  chlorineDioxideRedMin:    1.0
};

export interface TemperatureRules {
  thresholds?: TemperatureThresholds;
  openRules?: {
    issueReasons?: string[];
    issueReasonsSeverity?: Record<string, string>; // Map issue reason to severity (Low, Medium, High)
  };
  closeRules?: {
    isAutomatic?: boolean;
    successfulChecksRequired?: number;
  };
}

export interface SamplingThresholds {
  copperGreenMin?: number;          // 0.2
  copperGreenMax?: number;          // 2.0
  copperAmberMin?: number;          // null
  copperAmberMax?: number;          // null
  silverGreenMin?: number;          // 0.02
  silverGreenMax?: number;          // 0.1
  silverAmberMin?: number;          // null
  silverAmberMax?: number;          // null
  tvcRedMin?: number;               // 100
  tvcGreenMin?: number;             // 0
  tvcGreenMax?: number;             // 99
  tvcAmberMin?: number;             // null
  tvcAmberMax?: number;             // null
  tvcPurpleMin?: number;            // null
  tvcPurpleMax?: number;            // null
  legionellaAmberMin?: number;      // 101
  legionellaAmberMax?: number;      // 999
  legionellaRedMin?: number;        // 1000
  legionellaGreenMin?: number;      // 0
  legionellaGreenMax?: number;      // 0
  legionellaPurpleMin?: number;     // 1
  legionellaPurpleMax?: number;     // 100
  pseudomonasRedAbove?: number;     // 0
  pseudomonasGreenMin?: number;     // 0
  pseudomonasGreenMax?: number;     // 0
  pseudomonasAmberMin?: number;     // null
  pseudomonasAmberMax?: number;     // null
  pseudomonasPurpleMin?: number;    // null
  pseudomonasPurpleMax?: number;    // null
}

export const DEFAULT_SAMPLING_THRESHOLDS: SamplingThresholds = {
  copperGreenMin:        0.2,
  copperGreenMax:        2.0,
  silverGreenMin:        0.02,
  silverGreenMax:        0.1,
  tvcRedMin:             100,
  tvcGreenMin:           0,
  tvcGreenMax:           99,
  legionellaAmberMin:    101,
  legionellaAmberMax:    999,
  legionellaRedMin:      1000,
  legionellaGreenMin:    0,
  legionellaGreenMax:    0,
  legionellaPurpleMin:   1,
  legionellaPurpleMax:   100,
  pseudomonasRedAbove:   0,
  pseudomonasGreenMin:   0,
  pseudomonasGreenMax:   0,
};

export interface SamplingRules {
  thresholds?: SamplingThresholds;
  openRules?: Record<string, Record<string, { enabled?: boolean; min?: number | null; max?: number | null }>>;
  closeRules?: Record<string, {
    isAutomatic?: boolean;
    successfulChecksRequired?: number;
  }>;
}

export interface FlushingRules {
  openRules?: {
    issueReasons?: string[];
    issueReasonsSeverity?: Record<string, string>; // Map issue reason to severity (Low, Medium, High)
  };
  closeRules?: {
    isAutomatic?: boolean;
    successfulChecksRequired?: number;
  };
}

export interface TankRules {
  tankInspection?: TankInspectionRules;
  tankClean?: TankCleanRules;
}

export interface TankInspectionRules {
  openRules?: {
    issueReasons?: string[];
    issueReasonsSeverity?: Record<string, string>;
  };
  closeRules?: {
    isAutomatic?: boolean;
    successfulChecksRequired?: number;
  };
}

export interface TankCleanRules {
  openRules?: {
    issueReasons?: string[];
    issueReasonsSeverity?: Record<string, string>;
  };
  closeRules?: {
    isAutomatic?: boolean;
    successfulChecksRequired?: number;
  };
}

export interface ShowerCleanRules {
  openRules?: {
    issueReasons?: string[];
    issueReasonsSeverity?: Record<string, string>; // Map issue reason to severity (Low, Medium, High)
  };
  closeRules?: {
    isAutomatic?: boolean;
    successfulChecksRequired?: number;
  };
}

export interface SiteRules {
  siteId: string;
  tmv?: TmvRules;
  temperatures?: TemperatureRules;
  sampling?: SamplingRules;
  flushing?: FlushingRules;
  tanks?: TankRules;
  showerClean?: ShowerCleanRules;
}

export interface NonConformity {
  ncReference?: string;
  siteId: string;
  buildingId: string;
  outletId: string;
  workListItemIds: string[];
  workListType: string;
  issueReason: string;
  severity?: string;
  responsibility?: string;
  status: string;
  createdDate: Timestamp;
  createdBy: string;
  successfulChecksCount?: number;
  notes?: string;
  closedDate?: Timestamp;
  closedBy?: string;
  closureNotes?: string;
}
